/**
 * <marquee-controls>
 *
 * Adds a persistent, keyboard-operable pause/resume control to the CSS-animated
 * testimonial marquee (WCAG 2.2.2 Pause, Stop, Hide).
 *
 * The marquee's motion comes from `animation: scroll ... infinite` on the two
 * `.wt-brands__marquee__content` tracks (assets/section-brands.css). Pausing is
 * done by toggling a single class on the marquee wrapper, which sets
 * `animation-play-state: paused` on both tracks.
 *
 * The pre-existing `:hover` pause is left in place; it is no longer the only
 * available mechanism.
 */
if (!customElements.get("marquee-controls")) {
  customElements.define(
    "marquee-controls",
    class MarqueeControls extends HTMLElement {
      connectedCallback() {
        this.marquee = this.querySelector(".wt-brands__marquee");
        this.toggle = this.querySelector(".wt-brands__marquee__toggle");

        if (!this.marquee || !this.toggle) return;

        this.onToggleClick = this.onToggleClick.bind(this);
        this.toggle.addEventListener("click", this.onToggleClick);

        // Honour a reduced-motion preference by starting paused. This is a
        // supporting measure only - it does not replace the control itself.
        const prefersReducedMotion =
          window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.setPaused(Boolean(prefersReducedMotion));
      }

      disconnectedCallback() {
        if (this.toggle) {
          this.toggle.removeEventListener("click", this.onToggleClick);
        }
      }

      onToggleClick() {
        this.setPaused(!this.isPaused);
      }

      setPaused(paused) {
        this.isPaused = paused;
        this.marquee.classList.toggle("wt-brands__marquee--paused", paused);
        // Toggle-button convention, matching the theme's own play-pause-button:
        // the accessible name stays static and aria-pressed carries the state.
        // pressed = motion is currently paused.
        this.toggle.setAttribute("aria-pressed", paused ? "true" : "false");
      }
    },
  );
}
